import { ApiTax, ApiTaxRate } from '@vend/types'
import uniqBy from 'lodash/uniqBy'
import { add, multiply, round, vn } from 'vend-number'

interface TaxRate {
  rate: string | number
}

/**
 * Calculate the total tax rate from given the collection of tax rate objects (to 5 decimal places).
 *
 * @param  taxRates An array of tax rate objects (object with a number 'rate' property) to total
 *
 * @returns The total tax rate for the given Tax info
 */
export function calculateTotalTaxRate(taxRates: Readonly<TaxRate[]>): string {
  if (!taxRates || !taxRates.length) {
    return '0'
  }

  const rawTotal = taxRates.reduce((total, tax) => add(total, tax.rate), 0)
  return round(rawTotal, 5)
}

/**
 * Calculate the tax on the given price with the given tax rate (to 5 decimal places).
 *
 * @param  price The price to calculate the tax on
 * @param  taxRate The tax rate to calculate the tax with
 *
 * @returns The tax amount for the given price
 */
export function calculateTax(
  price: string | number,
  taxRate: string | number
): string {
  return round(multiply(price, taxRate), 5)
}

/**
 * Calculate tax component unit amount based on the given unit price and tax rate (& rules).
 *
 * @param  taxRate Rate object
 * @param  unitPrice Unit price of the product
 * @param  taxCode Tax code of the product
 *
 * @returns the tax component unit amount based on the given unit price and tax rate (& rules)
 */
export function calculateTaxComponentUnitAmount(
  taxRate: Readonly<Pick<ApiTaxRate, 'rate' | 'rules'>>,
  unitPrice: number | string,
  taxCode: string
): number {
  const { rate, rules } = taxRate
  const unitPriceVn = vn(unitPrice)

  if (
    rules?.length &&
    rules[0].type === 'threshold' &&
    taxCode === rules[0].tax_code
  ) {
    if (rules[0].tax_over_threshold && unitPriceVn.gte(rules[0].value)) {
      // Tax only over the threshold value
      return multiply(unitPriceVn.minus(rules[0].value), rate)
    } else if (unitPriceVn.lt(rules[0].value)) {
      return 0
    }
  }

  return multiply(unitPriceVn, rate)
}

/**
 * Collects the tax rates from passed array of taxes and returns the unique ones
 *
 * @param taxes
 * @returns Unique tax rates
 */
export function collectUniqueTaxRates(taxes: ApiTax[]): ApiTaxRate[] {
  if (taxes.length === 0) {
    return []
  }

  const rates = taxes.flatMap(tax => tax.rates)
  return uniqBy(rates, 'id')
}
